import { useLayoutEffect, useRef, type RefObject } from 'react'
import { useLocation } from 'react-router-dom'
import { APP_SHELL_HEADER_HEIGHT_PX } from '@/shared/components/AppShell/appShellLayout'

export const APP_SHELL_MAIN_TEST_ID = 'app-shell-main'

let scrollRestorationDisabled = false

/** Prevent the browser from restoring scroll on back/forward navigation. */
export function disableBrowserScrollRestoration(): void {
  if (scrollRestorationDisabled || globalThis.window === undefined) {
    return
  }
  if ('scrollRestoration' in globalThis.history) {
    globalThis.history.scrollRestoration = 'manual'
  }
  scrollRestorationDisabled = true
}

export function getAppShellMainElement(): HTMLElement | null {
  const el = document.querySelector(`[data-testid="${APP_SHELL_MAIN_TEST_ID}"]`)
  return el instanceof HTMLElement ? el : null
}

function canScrollVertically(container: HTMLElement): boolean {
  return container.scrollHeight > container.clientHeight + 1
}

export function scrollAppShellMainToTop(mainElement?: HTMLElement | null): void {
  disableBrowserScrollRestoration()

  const main = mainElement ?? getAppShellMainElement()
  if (main) {
    main.scrollTop = 0
    main.scrollLeft = 0
  }

  globalThis.window?.scrollTo(0, 0)
}

/** Reset scroll immediately and again after paint (lazy routes, scroll anchoring). */
export function scrollAppShellMainToTopWithRetry(mainElement?: HTMLElement | null): void {
  scrollAppShellMainToTop(mainElement)
  requestAnimationFrame(() => {
    scrollAppShellMainToTop(mainElement)
  })
}

export type ScrollAppShellMainToElementOptions = {
  behavior?: ScrollBehavior
  /** Extra space above the target (px) — keeps section titles visible below the scroll top. */
  offsetTop?: number
}

/** Smooth-scroll so `element` sits near the top of the visible page (main or window). */
export function scrollAppShellMainToElement(
  element: HTMLElement,
  options?: ScrollAppShellMainToElementOptions,
): void {
  const behavior = options?.behavior ?? 'smooth'
  const offsetTop = options?.offsetTop ?? 24
  const main = getAppShellMainElement()
  const elementRect = element.getBoundingClientRect()

  if (main && canScrollVertically(main)) {
    const mainRect = main.getBoundingClientRect()
    const targetTop = main.scrollTop + (elementRect.top - mainRect.top) - offsetTop
    main.scrollTo({ top: Math.max(0, targetTop), behavior })
    return
  }

  const stickyHeaderOffset = APP_SHELL_HEADER_HEIGHT_PX + offsetTop
  const targetTop = window.scrollY + elementRect.top - stickyHeaderOffset
  window.scrollTo({ top: Math.max(0, targetTop), behavior })
}

/** Resets AppShell `<main>` scroll on every navigation (including revisits). */
export function useScrollAppShellMainToTop(): RefObject<HTMLElement> {
  const mainRef = useRef<HTMLElement>(null)
  const { pathname, search, key } = useLocation()

  useLayoutEffect(() => {
    scrollAppShellMainToTopWithRetry(mainRef.current)
  }, [pathname, search, key])

  return mainRef
}

/** Scroll to top when a dashboard route mounts or its identity params change. */
export function useScrollToTopOnMount(routeIdentity: string): void {
  useLayoutEffect(() => {
    scrollAppShellMainToTopWithRetry()
  }, [routeIdentity])
}
